/**
 * Metrics Service Layer
 *
 * Provides a clean, type-safe interface for metrics operations.
 * Used by React Query hooks for data fetching and mutations.
 */

import { apiClient } from './api.client';
import type {
  PublicMetric,
  Metric,
  MetricTrend,
  CreateMetricDTO,
  UpdateMetricDTO,
  MetricFilters,
} from '@lnr-v2/types';

export interface MetricsResponse {
  metrics: Metric[];
  total: number;
  page?: number;
  limit?: number;
}

/**
 * Build query params from filters
 */
function buildSearchParams(filters?: MetricFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  }
  return params;
}

/**
 * Metrics Service
 * All metrics-related API operations
 */
export const metricsService = {
  /**
   * Get public metrics (no auth required)
   */
  async getPublic(): Promise<PublicMetric[]> {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/metrics/public`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch public metrics');
    }
    const data = await response.json();
    return data.metrics;
  },

  /**
   * Get all metrics with filters (auth required)
   */
  async getAll(filters?: MetricFilters): Promise<MetricsResponse> {
    const params = buildSearchParams(filters);
    return apiClient
      .get(`metrics?${params.toString()}`)
      .json<MetricsResponse>();
  },

  /**
   * Get a single metric by ID
   */
  async getById(id: string): Promise<Metric> {
    return apiClient.get(`metrics/${id}`).json<Metric>();
  },

  /**
   * Get metric trend data
   */
  async getTrend(
    id: string,
    period: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'
  ): Promise<MetricTrend> {
    return apiClient
      .get(`metrics/${id}/trend?period=${period}`)
      .json<MetricTrend>();
  },

  /**
   * Create a new metric
   */
  async create(data: CreateMetricDTO): Promise<Metric> {
    return apiClient.post('metrics', { json: data }).json<Metric>();
  },

  /**
   * Update an existing metric
   */
  async update(id: string, data: Partial<UpdateMetricDTO>): Promise<Metric> {
    return apiClient.put(`metrics/${id}`, { json: data }).json<Metric>();
  },

  /**
   * Delete a metric
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`metrics/${id}`);
  },

  /**
   * Bulk delete metrics
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await apiClient.delete('metrics/bulk', { json: { ids } });
  },

  /**
   * Bulk update metrics
   */
  async bulkUpdate(
    ids: string[],
    updates: Partial<UpdateMetricDTO>
  ): Promise<Metric[]> {
    return apiClient
      .put('metrics/bulk', { json: { ids, updates } })
      .json<Metric[]>();
  },
};
